<template>
  <div
    v-if="riskTier"
    v-on-click-outside="onClickOutsideHandler"
    class="relative"
  >
    <div
      class="flex max-w-63 items-center justify-center space-x-3 rounded-full border border-gray-200 px-4 py-1 font-semibold whitespace-nowrap"
      :class="{
        'cursor-pointer': !readonly,
        'cursor-default': readonly,
      }"
      @click="readonly ? undefined : (showSelector = !showSelector)"
    >
      <icon-base
        v-if="riskTier?.indicator"
        class="-translate-y-0-25 transform"
        height="18"
        width="4"
        :icon="'risk-tier-' + riskTier.indicator"
      />
      <span
        v-if="!reduce"
        :class="fontSize"
        class="overflow-hidden text-ellipsis"
      >
        {{ scorecardRtText || "-" }}
      </span>
      <div v-if="!readonly" class="cursor-pointer" @click.stop="handleClick">
        <icon-base
          width="10"
          height="10"
          :icon="IconXSmall"
          icon-color="gray"
        />
      </div>
    </div>
    <risk-tier-selector-modal
      v-if="showSelector"
      ref="riskTierSelector"
      :rt-to-edit="riskTier"
      :current-points="currentPoints || 0"
      @update="handleEdit"
      @close="showSelector = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import isEmpty from "lodash/isEmpty";

import type { MaybeArray } from "@/models/common";
import type { IScorecardRiskTier } from "@/models/scorecards";
import type { PropType } from "vue";
</script>

<script setup lang="ts">
import IconXSmall from "@/components/icons/IconXSmall.vue";
import RiskTierSelectorModal from "@/components/scorecards/RiskTierSelectorModal.vue";

const emit = defineEmits<{
  (eventName: "update:modelValue", rts: IScorecardRiskTier[]): void;
  (eventName: "remove", id: string): void;
}>();

const props = defineProps({
  riskTier: {
    type: Object as PropType<IScorecardRiskTier | null>,
    required: true,
  },
  fontSize: {
    type: String,
    default: "text-sm",
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  reduce: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [
      Array,
      Object,
      null,
    ] as PropType<MaybeArray<IScorecardRiskTier> | null>,
  },
  currentPoints: {
    type: Number,
  },
});

const { t } = useI18n();

const showSelector = ref(false);

const scorecardRtText = computed(() =>
  props.readonly
    ? props.riskTier?.name
    : t("SCORECARDS.RISK_TIER_DISPLAY", {
        name: props.riskTier?.name,
        from: props.riskTier?.points_from,
        to: props.riskTier?.points_to,
      })
);

const handleEdit = (riskTier: IScorecardRiskTier) => {
  if (props.readonly || isEmpty(props.modelValue)) {
    return;
  }

  const modelValue = Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue];

  const updatedRtIndex = modelValue.findIndex((rt) => rt.id === riskTier.id);
  const payload =
    updatedRtIndex > -1
      ? [
          ...[...modelValue].filter((value) => value.id !== riskTier.id),
          riskTier,
        ]
      : [...modelValue];
  emit("update:modelValue", payload);
};

const handleClick = () => {
  if (!props.riskTier?.id) {
    return;
  }
  emit("remove", props.riskTier?.id);
};

const onClickOutsideHandler = [
  () => (showSelector.value = false),
  { capture: false },
];
</script>
